package top.wcpe.allincore.dsl.compiler.parse

import top.wcpe.allincore.dsl.compiler.ast.ComponentAst
import top.wcpe.allincore.dsl.compiler.ast.ExprAst
import top.wcpe.allincore.dsl.compiler.diag.DslError
import top.wcpe.allincore.dsl.compiler.lex.Token
import top.wcpe.allincore.dsl.compiler.lex.TokenType

/**
 * 解析 `props { ... }` 块，返回 prop 声明列表。
 *
 * 语法：
 * ```
 * PropsBlock ::= 'props' '{' PropDecl* '}'
 * PropDecl   ::= IDENT ':' Type ('=' Literal)? NEWLINE
 * Type       ::= 'String' | 'Number' | 'Boolean' | 'Bool' | 'Color' | 'Function' | 'Fun'
 *              | DataTypeName
 * DataTypeName ::= 标识符（PascalCase，指向顶层 data 声明）
 * ```
 *
 * 类型名严格使用 PascalCase。`Bool` 是 `Boolean` 的别名，`Fun` 是 `Function` 的别名；
 * 大小写归一化由 [Lexer] 完成（多个字面量映射到同一 KW_* TokenType）。
 *
 * M6 review B：data 类型 prop（如 `item: Item`）由解析期识别，目标 data 名存入
 * [ComponentAst.PropDecl.dataTypeName]；data 类型 prop 暂不允许写默认值（E755）。
 */
internal class PropsBlockParser(private val p: Parser) {

    companion object {
        private const val MAX_COMPONENT_PROPS = 16
    }

    /** TokenType 到 PropType 的映射（Lexer 已把 PascalCase/小写归一化到同一 TokenType）。 */
    private val typeKeywords = mapOf(
        TokenType.KW_STRING to ComponentAst.PropType.STRING,
        TokenType.KW_NUMBER to ComponentAst.PropType.NUMBER,
        TokenType.KW_BOOL to ComponentAst.PropType.BOOL,
        TokenType.KW_COLOR to ComponentAst.PropType.COLOR,
        TokenType.KW_FUN to ComponentAst.PropType.FUN,
    )

    fun parse(): List<ComponentAst.PropDecl> {
        p.expect(TokenType.KW_PROPS, "缺少 props 关键字")
        p.expect(TokenType.LBRACE, "props 块缺少 '{'")
        p.skipNewlines()

        val decls = mutableListOf<ComponentAst.PropDecl>()
        val seen = mutableSetOf<String>()

        while (!p.check(TokenType.RBRACE) && !p.check(TokenType.EOF)) {
            val identTok = expectPropName()
            val name = identTok.lexeme

            validatePropName(identTok)
            if (decls.size >= MAX_COMPONENT_PROPS) {
                p.warn(
                    DslError.AUI_V2_PUBLIC_PROPS_SOFT_LIMIT,
                    identTok.position,
                    decls.size + 1,
                    MAX_COMPONENT_PROPS,
                )
            }

            if (!seen.add(name)) {
                p.fail("props 中存在重名声明 '$name'", identTok.position)
            }

            p.expect(TokenType.COLON, "prop '$name' 缺少 ':'")

            val (propType, dataTypeName) = parsePropType(name)

            // 解析可选默认值：data 类型 prop 暂不支持默认值（E755）
            val defaultValue = if (p.match(TokenType.EQUALS)) {
                if (propType == ComponentAst.PropType.DATA) {
                    p.fail(DslError.E755_DATA_PROP_DEFAULT_NOT_ALLOWED, identTok.position, name)
                }
                parseDefaultValue(name, propType)
            } else {
                null
            }

            decls += ComponentAst.PropDecl(
                name = name,
                type = propType,
                defaultValue = defaultValue,
                position = identTok.position,
                dataTypeName = dataTypeName,
            )

            // 消费行尾：NEWLINE 或即将遇到 RBRACE 均合法
            if (!p.check(TokenType.RBRACE)) {
                p.expect(TokenType.NEWLINE, "prop 声明末尾缺少换行")
                p.skipNewlines()
            }
        }

        p.expect(TokenType.RBRACE, "props 块缺少 '}'")
        return decls
    }

    /**
     * 读取 prop 名 token：IDENTIFIER 或类型关键字均合法。
     * 类型关键字出现在"名字位置"时语义上是普通标识符，不影响后续类型声明。
     */
    private fun expectPropName(): Token {
        val cur = p.current()
        if (cur.type == TokenType.IDENTIFIER || cur.type in typeKeywords) {
            return p.advance()
        }
        p.fail("props 声明缺少属性名：期望 IDENTIFIER，实际为 ${cur.type}（'${cur.lexeme}'）", cur.position)
    }

    /** 校验 prop 名必须是 lowerCamelCase，且不能用连字符拼接。 */
    private fun validatePropName(nameTok: Token) {
        if (!isLowerCamelCase(nameTok.lexeme) || p.check(TokenType.MINUS)) {
            p.fail("prop 名必须使用 lowerCamelCase，实际为 '${nameTok.lexeme}'", nameTok.position)
        }
    }

    private fun isLowerCamelCase(name: String): Boolean {
        if (name.isEmpty() || !name[0].isLowerCase()) return false
        return name.all { it.isLetterOrDigit() }
    }

    /**
     * 解析 prop 的类型 Token，返回 (PropType, dataTypeName?)。
     *
     * 三类来源：
     * - typeKeywords 命中：5 类基础内置类型，dataTypeName 为 null
     * - IDENTIFIER 且首字母大写：视为 data 类型引用，dataTypeName 即为 lexeme
     * - 其它：报错（小写标识符 / 非法 Token）
     */
    private fun parsePropType(name: String): Pair<ComponentAst.PropType, String?> {
        val typeTok = p.current()
        val builtin = typeKeywords[typeTok.type]
        if (builtin != null) {
            // 基础内置类型仍需拒绝小写形式（保持与 5 类一致的 PascalCase 约束）
            if (typeTok.lexeme.isNotEmpty() && typeTok.lexeme[0].isLowerCase()) {
                p.fail(
                    "prop '$name' 类型名必须使用 PascalCase（首字母大写），实际为 '${typeTok.lexeme}'",
                    typeTok.position,
                )
            }
            p.advance()
            return builtin to null
        }
        if (typeTok.type == TokenType.IDENTIFIER) {
            // 标识符：首字母必须大写，作为 data 类型引用接受
            if (typeTok.lexeme.isEmpty() || !typeTok.lexeme[0].isUpperCase()) {
                p.fail(
                    "prop '$name' 类型名必须使用 PascalCase（首字母大写），实际为 '${typeTok.lexeme}'",
                    typeTok.position,
                )
            }
            p.advance()
            return ComponentAst.PropType.DATA to typeTok.lexeme
        }
        p.fail(
            "prop '$name' 类型无效，期望 String/Number/Boolean(Bool)/Color/Function(Fun) 或 data 类型名，实际为 '${typeTok.lexeme}'",
            typeTok.position,
        )
    }

    /**
     * 解析默认值字面量并校验类型匹配（DSL_E310）。
     * 不走 ExpressionParser，仅接受直接字面量 token。
     *
     * Color 类型既接受字符串字面量（`"#RRGGBB"` / `"#AARRGGBB"`），
     * 也接受 Number 字面量（`0xFF42A5F5` 等 ARGB int）。
     */
    private fun parseDefaultValue(name: String, type: ComponentAst.PropType): ExprAst {
        // theme.<key> 主题 token 作默认值：交由 ExpressionParser 解析为 ThemeRef（含多层 dotted key），
        // 与元素属性中的 theme 引用走同一路径；具体类型在主题解析期校验，解析期不按字面量类型拦截。
        if (p.check(TokenType.KW_THEME)) {
            return ExpressionParser(p).parse()
        }
        // number 单独处理前导负号
        if (type == ComponentAst.PropType.NUMBER) return parseNumberDefault(name)
        // color 既接受字符串字面量，也接受 Number 字面量
        if (type == ComponentAst.PropType.COLOR) return parseColorDefault(name)

        val litTok = p.current()
        val expected = expectedTokenType(type)
        if (litTok.type != expected) failE310(name, type, litTok)
        p.advance()

        return when (type) {
            ComponentAst.PropType.STRING ->
                ExprAst.Literal.StringLit(litTok.literalValue as String, litTok.position)
            ComponentAst.PropType.BOOL ->
                ExprAst.Literal.BoolLit(litTok.literalValue as Boolean, litTok.position)
            ComponentAst.PropType.FUN ->
                ExprAst.NullLit(litTok.position)
            else -> failE310(name, type, litTok)
        }
    }

    /** number 默认值：支持可选前导 `-` */
    private fun parseNumberDefault(name: String): ExprAst {
        val negative = p.match(TokenType.MINUS)
        val numTok = p.current()
        if (numTok.type != TokenType.NUMBER) failE310(name, ComponentAst.PropType.NUMBER, numTok)
        p.advance()
        val raw = (numTok.literalValue as Number).toDouble()
        return ExprAst.Literal.NumberLit(if (negative) -raw else raw, numTok.position)
    }

    /** color 默认值：String 字面量（`"#RRGGBB"`）或 Number 字面量（ARGB int）。 */
    private fun parseColorDefault(name: String): ExprAst {
        val litTok = p.current()
        return when (litTok.type) {
            TokenType.STRING -> {
                p.advance()
                ExprAst.Literal.StringLit(litTok.literalValue as String, litTok.position)
            }
            TokenType.NUMBER -> {
                p.advance()
                val raw = (litTok.literalValue as Number).toDouble()
                ExprAst.Literal.NumberLit(raw, litTok.position)
            }
            else -> failE310(name, ComponentAst.PropType.COLOR, litTok)
        }
    }

    /** 非 number / color 类型对应的期望 token 类型 */
    private fun expectedTokenType(type: ComponentAst.PropType): TokenType = when (type) {
        ComponentAst.PropType.STRING -> TokenType.STRING
        ComponentAst.PropType.BOOL -> TokenType.BOOLEAN
        ComponentAst.PropType.FUN -> TokenType.NULL
        ComponentAst.PropType.NUMBER -> error("number 由专用分支处理")
        ComponentAst.PropType.COLOR -> error("color 由专用分支处理")
        // DATA prop 不允许默认值，调用前已经在 parse() 阶段以 E755 拦下，理论上不会进入此分支。
        ComponentAst.PropType.DATA -> error("data 类型 prop 不应进入默认值解析（应已被 E755 拦下）")
    }

    /** 抛出 DSL_E310 错误 */
    private fun failE310(name: String, type: ComponentAst.PropType, litTok: Token): Nothing {
        p.fail(DslError.E310_DEFAULT_VALUE_TYPE_MISMATCH, litTok.position, name, type.name.lowercase())
    }
}
